import React, { useEffect, useMemo, useState } from "react";
import Shell from "@/components/Shell";
import { PaymentLinkButton } from "@/components/outreach/Payments";
import { ProposalButton } from "@/components/outreach/Proposal";
import { ShowingButton, DocumentsButton, DealBuyerBadges } from "@/components/outreach/BuyerActions";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, LayoutGrid, Table as TableIcon, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { OnboardingPanel } from "@/components/Onboarding";

const stageColor = {
  slate: "border-l-slate-500", blue: "border-l-blue-500", violet: "border-l-violet-500",
  amber: "border-l-amber-500", emerald: "border-l-emerald-500", rose: "border-l-rose-500",
};

export default function Deals() {
  const [pipelines, setPipelines] = useState([]);
  const [deals, setDeals] = useState([]);
  const [docs, setDocs] = useState({});
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("kanban");

  const loadDocs = () => api.get("/documents").then((r) => { const m = {}; for (const d of r.data) (m[d.deal_id] = m[d.deal_id] || []).push(d); setDocs(m); }).catch(() => {});
  const load = async () => {
    const [p, d] = await Promise.all([api.get("/pipelines"), api.get("/deals")]);
    setPipelines(p.data);
    setDeals(d.data);
    loadDocs();
  };
  useEffect(() => { load(); }, []);

  const pipeline = pipelines[0];
  const stages = pipeline?.stages || [];
  const dealsByStage = useMemo(() => {
    const map = {};
    for (const s of stages) map[s.id] = [];
    for (const d of deals) if (map[d.stage_id]) map[d.stage_id].push(d);
    return map;
  }, [deals, stages]);

  const onDrop = async (e, stageId) => {
    e.preventDefault();
    e.currentTarget.classList.remove("drag-over");
    const dealId = e.dataTransfer.getData("deal_id");
    if (!dealId) return;
    try {
      const { data } = await api.post(`/deals/${dealId}/move`, { stage_id: stageId });
      setDeals((prev) => prev.map((d) => (d.id === dealId ? data : d)));
      toast.success("Deal moved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const pipelineValue = deals.filter((d) => d.status === "open").reduce((a, b) => a + (b.value || 0), 0);

  return (
    <Shell title="Deals" actions={
      pipeline && (
        <>
          <div className="hidden sm:flex items-center gap-1.5 text-xs mono mr-2 px-2.5 py-1 rounded-full border border-border bg-card">
            <DollarSign className="w-3.5 h-3.5 text-primary" /><span className="font-semibold">{pipelineValue.toLocaleString()}</span><span className="text-muted-foreground">open pipeline</span>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" data-testid="btn-new-deal"><Plus className="w-4 h-4 mr-2" />New deal</Button></DialogTrigger>
            <NewDealDialog pipeline={pipeline} stages={stages} onDone={() => { setOpen(false); load(); }} />
          </Dialog>
        </>
      )
    }>
      <OnboardingPanel />
      <Tabs value={tab} onValueChange={setTab} className="p-6">
        <TabsList>
          <TabsTrigger value="kanban" data-testid="tab-kanban"><LayoutGrid className="w-4 h-4 mr-1.5" />Kanban</TabsTrigger>
          <TabsTrigger value="table" data-testid="tab-table"><TableIcon className="w-4 h-4 mr-1.5" />Table</TabsTrigger>
        </TabsList>
        <TabsContent value="kanban" className="mt-4">
          <div className="flex gap-4 overflow-x-auto pb-3 stagger">
            {stages.map((s) => (
              <div
                key={s.id}
                className={`kanban-col flex-shrink-0 w-72 border border-border rounded-lg bg-muted/30 transition-[background-color,border-color,box-shadow] duration-200`}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("drag-over"); }}
                onDragLeave={(e) => e.currentTarget.classList.remove("drag-over")}
                onDrop={(e) => onDrop(e, s.id)}
                data-testid={`stage-col-${s.name.toLowerCase()}`}
              >
                <div className="px-3 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[13px] font-semibold"><span className={`w-2 h-2 rounded-full ${s.color === 'slate' ? 'bg-slate-500' : s.color === 'blue' ? 'bg-blue-500' : s.color === 'violet' ? 'bg-violet-500' : s.color === 'amber' ? 'bg-amber-500' : s.color === 'emerald' ? 'bg-emerald-500' : 'bg-rose-500'}`} />{s.name}</div>
                  <div className="flex items-center gap-2 text-[11px] mono text-muted-foreground"><span>${(dealsByStage[s.id] || []).reduce((a, b) => a + (b.value || 0), 0).toLocaleString()}</span><span className="px-1.5 py-px rounded bg-background border border-border">{dealsByStage[s.id]?.length || 0}</span></div>
                </div>
                <div className="px-2 pb-2 space-y-2 min-h-[200px]">
                  {(dealsByStage[s.id] || []).map((d) => (
                    <div
                      key={d.id}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData("deal_id", d.id)}
                      className={`lift border border-border border-l-[3px] ${stageColor[s.color]} rounded-md bg-card p-3 cursor-grab active:cursor-grabbing shadow-[var(--shadow-card)]`}
                      data-testid={`deal-card-${d.id}`}
                    >
                      <div className="text-[13px] font-semibold leading-snug">{d.title}</div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="text-xs mono font-medium">${(d.value || 0).toLocaleString()}</div>
                        <div className="flex items-center gap-1"><DocumentsButton deal={d} docs={docs[d.id]} compact onChanged={loadDocs} /><ShowingButton deal={d} compact /><ProposalButton deal={d} compact /><PaymentLinkButton deal={d} compact onCreated={load} /><div className="text-[10px] mono text-muted-foreground px-1.5 py-px rounded-full bg-muted">{d.probability}%</div></div>
                      </div>
                      {d.estimated_rebate != null && <div className={`mt-1.5 text-[10px] mono flex items-center gap-1.5 ${d.price_eligible === false ? "text-amber-500" : "text-emerald-500"}`} data-testid={`deal-rebate-${d.id}`}>Rebate ≈ ${Math.round(d.estimated_rebate).toLocaleString()}{d.state ? ` · ${d.state}` : ""}{d.price_eligible === false ? " · under min price" : ""}</div>}
                      <DealBuyerBadges deal={d} docs={docs[d.id]} />
                    </div>
                  ))}
                  {(dealsByStage[s.id] || []).length === 0 && <div className="h-20 rounded-md border border-dashed border-border/70 flex items-center justify-center text-[11px] text-muted-foreground">Drop deals here</div>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="table" className="mt-4">
          <div className="border border-border rounded-lg overflow-hidden bg-card">
            <table className="cf-table w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left py-2.5 px-3">Deal</th>
                  <th className="text-left py-2.5 px-3">Stage</th>
                  <th className="text-right py-2.5 px-3">Value</th>
                  <th className="text-right py-2.5 px-3">Probability</th>
                  <th className="text-left py-2.5 px-3">Status</th>
                  <th className="text-left py-2.5 px-3">Buyer</th>
                </tr>
              </thead>
              <tbody>
                {deals.map((d) => {
                  const s = stages.find((x) => x.id === d.stage_id);
                  return (
                    <tr key={d.id} data-testid={`deal-row-${d.id}`}>
                      <td className="py-2 px-3 font-medium">{d.title}</td>
                      <td className="py-2 px-3">{s?.name}</td>
                      <td className="py-2 px-3 text-right mono">${(d.value || 0).toLocaleString()}</td>
                      <td className="py-2 px-3 text-right mono text-muted-foreground">{d.probability}%</td>
                      <td className="py-2 px-3 capitalize text-muted-foreground">{d.status}</td>
                      <td className="py-2 px-3"><DealBuyerBadges deal={d} docs={docs[d.id]} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </Shell>
  );
}

function NewDealDialog({ pipeline, stages, onDone }) {
  const [form, setForm] = useState({ title: "", value: "", stage_id: stages[0]?.id || "", pipeline_id: pipeline.id, probability: 10, target_price: "", state: "", lender: "", commission_pct: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/deals", { ...form, value: Number(form.value || 0), probability: Number(form.probability || 10), target_price: form.target_price ? Number(form.target_price) : null, commission_pct: form.commission_pct ? Number(form.commission_pct) : null, state: form.state || null, lender: form.lender || null });
      toast.success("Deal created");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };
  return (
    <DialogContent>
      <DialogHeader><DialogTitle>New deal</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div className="space-y-2"><Label>Title</Label><Input data-testid="deal-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label>Value ($)</Label><Input data-testid="deal-value" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></div>
          <div className="space-y-2"><Label>Probability (%)</Label><Input data-testid="deal-probability" type="number" value={form.probability} onChange={(e) => setForm({ ...form, probability: e.target.value })} /></div>
          <div className="col-span-2 grid grid-cols-2 gap-3 border-t border-border pt-3" data-testid="deal-buyer-fields">
            <div className="space-y-2"><Label>Target home price ($)</Label><Input data-testid="deal-target-price" type="number" value={form.target_price} onChange={(e) => setForm({ ...form, target_price: e.target.value })} placeholder="450000" className="mono" /></div>
            <div className="space-y-2"><Label>Seller-paid buyer commission (%)</Label><Input data-testid="deal-commission" type="number" step="0.1" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} placeholder="2.5" className="mono" /></div>
            <div className="space-y-2"><Label>State</Label><Input data-testid="deal-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase().slice(0, 2) })} placeholder="PA" className="mono uppercase" /></div>
            <div className="space-y-2"><Label>Lender</Label><Input data-testid="deal-lender" value={form.lender} onChange={(e) => setForm({ ...form, lender: e.target.value })} placeholder="e.g. Rocket Mortgage" /></div>
            <div className="col-span-2 text-xs text-muted-foreground">Estimated rebate is computed automatically (price × commission − flat fee) and saved on the deal.</div>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Stage</Label>
          <Select value={form.stage_id} onValueChange={(v) => setForm({ ...form, stage_id: v })}>
            <SelectTrigger data-testid="deal-stage"><SelectValue /></SelectTrigger>
            <SelectContent>{stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button type="submit" data-testid="deal-submit" disabled={loading} className="w-full">{loading ? "Creating..." : "Create deal"}</Button>
      </form>
    </DialogContent>
  );
}
